// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// The design binding's `kbSops` card grid. Cards are the SOP Factory register
// (`listSops`, `artifact_type=sop`) — the same source SOP governance's register
// reads — projected straight, not seeded: no title or category here is invented
// when upstream leaves it blank.
//
// Each card renders at once. The step line shows only when the list response
// carries `step_count` (SOP items only); otherwise it is left out, with no
// placeholder and no per-card fetch.

import React from 'react';
import { Link } from 'react-router-dom';
import type { ArtifactSummary } from '../../types';
import StepCountIcon from './StepCountIcon';
import { sopDetailPath } from '../../services/sopPath';

function SopCard({ sop, siteId }: { sop: ArtifactSummary; siteId?: string }) {
  const category = sop.categories?.domain ?? sop.categories?.procedure_type;
  const count = sop.step_count;
  return (
    <Link
      to={sopDetailPath(sop.artifact_id, siteId)}
      className="sl-card"
      data-testid="sop-card"
      data-sop-id={sop.artifact_id}
    >
      <div className="sl-card-head">
        <span className="sl-card-id" data-testid="sop-id">
          {sop.artifact_id}
        </span>
        {category && <span className="sl-card-category">{category}</span>}
      </div>
      <div className="sl-card-title" data-testid="sop-title">
        {sop.title ?? sop.artifact_id}
      </div>
      {/* No summary field exists on ArtifactSummary or the full Artifact — the
          design's one-line description is not in any upstream contract. */}
      {typeof count === 'number' && (
        <div className="sl-card-foot">
          <span className="sl-card-steps">
            <StepCountIcon />
            <span data-testid="sop-step-count">{`${count} step${count === 1 ? '' : 's'}`}</span>
          </span>
        </div>
      )}
    </Link>
  );
}

/** `siteId`: the site the list was read for, carried in each card's link (ADM reads per site). */
export default function SopLibraryGrid({
  sops,
  siteId,
}: {
  sops: ArtifactSummary[];
  siteId?: string;
}) {
  return (
    <div className="sl-grid">
      {sops.map(s => (
        <SopCard key={s.artifact_id} sop={s} siteId={siteId} />
      ))}
    </div>
  );
}
